import { useMemo } from 'react';

// ─────────────────────────────────────────────────────────────────────────
// CSC Proposal math — originally mirrored from the 181.76kwp.xlsx Excel
// (Input / Generation / Breakeven / Environmental sheets), but PEAK_SUN_HOURS
// was bumped from 3.3 → 3.4 on 2026-05-21 to match the Bill tab + System Size
// calculator (useCscBillProposal.YIELD_PER_KWP, commercialSolarCalculator).
// One source of truth: every CSC tab now reports the same kWh/RM numbers.
//
// Sample outputs (181.76 kWp · LV tariff 0.515 · RM 1800/kWp · O&M RM 12,478):
//   Year-1 gen    = kwp × 3.4 × 365 = 225,562.16 kWh
//   Year-N gen    = yr1 × (0.98 − 0.007 × (N−1)) / 0.98
//   Year-2 CA tax = cost × 0.34 × 0.24                (claim starts year after install)
// IRR/savings/payback/LCOE all scale ~3% higher than the original Excel
// reference because of the +0.1 PSH bump.
// ─────────────────────────────────────────────────────────────────────────

// ── Generation constants (Input sheet) ──
export const PEAK_SUN_HOURS = 3.4;            // Aligned to Bill tab + Calc (was 3.3 / Input!D11)
const INITIAL_DERATE_YEAR_1 = 0.98;            // Generation!C9 — 2% Y1 loss
const ANNUAL_DEGRADATION = 0.007;              // Generation!B10:B28 — 0.7%/yr linear
export const PROJECTION_YEARS = 20;            // Generation rows 9-28
const SARE_SMP_RATE = 0.20;                    // RM/kWh — exported surplus sellback (SMP)

// ── Environmental constants (Input!J30:J31) ──
const CO2_TONNES_PER_MWH = 0.55;               // CO2 = 0.55 × annualGen / 1000
const TREES_PER_TONNE_CO2 = 1000 / 87;          // ≈ 11.49 trees per tonne

// ── Tax (Breakeven sheet) ──
const CORPORATE_TAX_RATE = 0.24;               // J2 = 0.24

// Capital Allowance schedule — fully absorbs Qualifying Expenditure over 6 years.
// Excel cells N20-N25 / O20-O25 / P20-P25 (rates per year of assessment):
//   IA 20% + AA 14% = 34% (initial claim year)
//   AA 14% each (4 follow-up years)
//   AA 10% (final year, balances to 100%)
// Note: claim does NOT start in the installation year. The customer can only
// begin claiming from the year AFTER installation (Y2 in our projection).
// Y1 in the table = installation year = no CA/GITA claim.
const CA_SCHEDULE = [0.34, 0.14, 0.14, 0.14, 0.14, 0.10];
const CA_FIRST_CLAIM_YEAR = 2;     // Y1 = installation, Y2 = first claim

// ── Defaults ──
export const DEFAULT_PRICE_PER_KWP = 1800;     // Excel Input!I8 = D9 × 1800
export const DEFAULT_OM_PER_KWP_PER_YEAR = 70; // ≈ Excel Input!F1 / kWp (12478 / 181.76 ≈ 68.7)

// O&M tier table — mirrors the CSC calculator's MAINT_TIERS so the proposal's
// default O&M matches what the salesperson sees on the calculator screen.
// Tier ceilings (kWp) and the flat RM/year rate for systems up to that ceiling.
const MAINT_TIERS = [
    { max: 6.82, rate: 1800 }, { max: 10.54, rate: 2300 }, { max: 20, rate: 2600 },
    { max: 40,   rate: 3500 }, { max: 60,    rate: 4500 }, { max: 80, rate: 4500 },
    { max: 100,  rate: 4500 }, { max: 300,   rate: 5000 }, { max: 600, rate: 7500 },
    { max: 1000, rate: 11000 },
];
export function getMaintenanceForKwp(kwp) {
    const k = Number(kwp) || 0;
    for (const t of MAINT_TIERS) if (k <= t.max) return t.rate;
    return 11000; // > 1000 kWp falls back to top tier
}
const DEFAULT_LOAN_RATE = 0.05;                // 5% p.a. (matches Astern bank-loan Excel)
const DEFAULT_LOAN_TERM_MONTHS = 84;           // 7 years × 12 = 84 months
const DEFAULT_SARE_TARIFF_DISCOUNT = 0.95;     // legacy fallback (only used if kWp = 0)

// ── SARE tariff brackets by system size (RM/kWh) ──
// Authoritative price list provided by Astern (May 2026). Smaller systems pay
// more per kWh because absolute monthly revenue is lower; >600 kWp earns the
// best (lowest) tariff.
//
// termYears = 10 charges a flat +8 sen premium on top of whatever the 20-year
// band is — the customer pays more per kWh in exchange for half the commitment
// (user 2026-08-23: "10years is +8sen from existing"). The premium rides on the
// BAND, not on a fixed 0.44, so it scales with system size the same way the
// 20-year table does.
export const SARE_10YR_TERM = 10;
export const SARE_15YR_TERM = 15;
export const SARE_20YR_TERM = 20;

/**
 * The SARE rate card: RM/kWh by system size and lease term. (user 2026-10-23)
 *
 * NOT a formula. I had these as a linear ladder off the 20-year rate - 15 years +0.04,
 * 10 years +0.08 - extrapolated from the one premium that existed in the code. The real
 * card does not work that way: the 15-year step is +0.015 for the first three bands but
 * +0.02 above 600 kWp, and the 10-year step is +0.055 throughout. A ladder that is right
 * in one band and wrong in another is worse than a table, because it looks principled.
 *
 * Bands are upper bounds in kWp. Below 40 kWp SARE is not offered at all.
 */
export const SARE_RATE_TABLE = [
    { maxKwp: 150, rates: { 20: 0.360, 15: 0.375, 10: 0.415 } },
    { maxKwp: 300, rates: { 20: 0.320, 15: 0.335, 10: 0.375 } },
    { maxKwp: 600, rates: { 20: 0.300, 15: 0.315, 10: 0.355 } },
    { maxKwp: Infinity, rates: { 20: 0.280, 15: 0.300, 10: 0.335 } },
];
export const SARE_MIN_KWP = 40;
/**
 * Add-ons raise the SARE rate, because SARE has to recover them over the contract too.
 *
 *     uplift = addOnCost / kWp / 10,000 / 0.8
 *
 * Read left to right: cost per kWp, then every RM100 of that adds RM0.01/kWh, then the
 * whole thing is grossed up by the 0.8 recovery factor.
 *
 *     RM7,400 on 40 kWp -> 185/kWp -> 0.0185 -> / 0.8 -> RM0.023125/kWh
 *     so the 40-150 kWp band quotes 0.360 + 0.0231 = RM0.3831/kWh
 *
 * THE 0.8 WAS MISSING UNTIL 2026-10-24 and the rate was quoted short because of it. On
 * that 40 kWp example it read 0.3785 against a correct 0.3831 - RM0.0046/kWh, about
 * RM230 a year and ~RM4,600 across the term, unrecovered on every SARE deal carrying an
 * add-on. The user produced the full formula on a calculator; the code had the first
 * three operations and stopped. (user 2026-10-24: "ya 0.3831 maybe use this formula")
 *
 * Deliberately NOT rounded down to whole hundreds - RM150/kWp lifts the rate by RM0.015,
 * not RM0.01. Rounding here would make the rate jump in steps and under-recover
 * everywhere in between. (user 2026-10-14)
 */
export const SARE_ADDON_RECOVERY_FACTOR = 0.8;

export function sareAddOnUplift(addOnCost, kwp) {
    const cost = Number(addOnCost) || 0;
    const k = Number(kwp) || 0;
    if (cost <= 0 || k <= 0) return 0;
    return Number(((cost / k / 100) * 0.01 / SARE_ADDON_RECOVERY_FACTOR).toFixed(4));
}

export function defaultSareTariffByKwp(kwp, termYears = 20, addOnCost = 0) {
    const n = Number(kwp) || 0;
    if (n < SARE_MIN_KWP) return 0;        // SARE is not offered below 40 kWp
    const band = SARE_RATE_TABLE.find(b => n <= b.maxKwp) || SARE_RATE_TABLE[SARE_RATE_TABLE.length - 1];
    const t = Number(termYears);
    const base = band.rates[t] != null ? band.rates[t] : band.rates[SARE_20YR_TERM];

    /* THE ADD-ON UPLIFT IS 20-YEAR ONLY. Add-ons raise the rate because SARE has to
       recover them over the contract, and a shorter contract plainly recovers them over
       fewer years - but the factor for 10 and 15 years has not been worked out yet.
       Applying the 20-year uplift to them would be a guess presented as a price.
       (user 2026-10-23: "add on sare rate follow back the rate formula but its for
        20years only. another 10 and 15years nt yet calculate")

       Returning the bare band rate here is therefore NOT a quotable price when add-ons
       exist - it is a rate that recovers nothing for them. The UI must withhold the 15-
       and 10-year terms entirely in that case rather than print this number, which is
       what cscCombinedTab does. (user 2026-10-24: "15 years and 10years disable first if
       got add on") */
    const uplift = t === SARE_20YR_TERM ? sareAddOnUplift(addOnCost, n) : 0;
    return Number((base + uplift).toFixed(4));
}

// CSC tariff list (kept in sync with the Bill tab's TARIFFS in cscBillTab.jsx)
export const CSC_PROPOSAL_TARIFFS = [
    { rate: 0.515, label: 'Low Voltage (LV)' },
    // Time-of-Use LV: peak/off-peak energy, capacity and network per kWh, and a
    // rebate line. Priced by calcCommercialLvTouBill, never bill ÷ rate.
    { rate: 0.4539, label: 'Low Voltage ToU (LV ToU)' },
    { rate: 0.303, label: 'Medium Voltage (MV)' },
    { rate: 0.437, label: 'High Voltage (HV)' },
    { rate: 0.451, label: 'Agriculture' },
];

// ─────────────────────────────────────────────────────────────────────────
// Pure formula helpers
// ─────────────────────────────────────────────────────────────────────────

/**
 * Year-N annual generation in kWh.
 *   Year 1: kwp × 3.4 × 365  (PSH aligned to Bill tab + Calc, 2026-05-21)
 *   Year N>1: yr1 × (0.98 − 0.007 × (N−1)) / 0.98
 */
export function annualGeneration(kwp, year) {
    const yr1 = kwp * PEAK_SUN_HOURS * 365;
    if (year <= 1) return yr1;
    const cumEff = INITIAL_DERATE_YEAR_1 - ANNUAL_DEGRADATION * (year - 1);
    return (yr1 * cumEff) / INITIAL_DERATE_YEAR_1;
}

/**
 * Capital Allowance claimable in year N (1-indexed). Returns 0 after Year 6.
 * CA × corporate tax rate = the tax saving deducted from cashflow.
 */
export function caForYear(systemCost, year) {
    // Year of installation (year 1) has no allowance — claim begins the year after.
    const idx = year - CA_FIRST_CLAIM_YEAR;
    if (idx < 0) return 0;
    const rate = CA_SCHEDULE[idx] ?? 0;
    return systemCost * rate;
}

/** PMT — annuity payment (Excel PMT function). */
export function pmt(monthlyRate, nMonths, principal) {
    if (!principal || nMonths <= 0) return 0;
    if (monthlyRate === 0) return principal / nMonths;
    return (principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -nMonths));
}

/**
 * A FLAT rate as its EFFECTIVE (reducing-balance) equivalent.
 *
 * Hire purchase is quoted flat — interest on the WHOLE principal for the whole tenure:
 *     monthly = (P + P x flat x years) / months
 * A bank term loan is quoted on the reducing balance, which is what pmt() above computes
 * and what every projection in this file consumes.
 *
 * Rather than teach the table two kinds of arithmetic, a flat rate is converted ONCE here
 * and everything downstream keeps using the single PMT engine. (user 2026-10-16: "if its
 * flatrate, then u shall convert b4 put into table. so table use one type of rate to
 * calculate")
 *
 * The conversion is independent of the principal — P cancels on both sides — so this is a
 * pure function of (flat, years). There is no closed form, so it bisects: f(r) = PMT(r) −
 * flatMonthly is strictly increasing in r, which makes bisection safe and quick. 80
 * iterations over 0–100% p.a. lands well inside a basis point.
 *
 * A flat rate is always LOWER than its effective twin — roughly 1.8-1.9x for ordinary
 * tenures, so 3.6% flat over 3 years is about 6.7% effective. Quoting a flat number as if
 * it were effective understates the cost, which is the mistake this exists to prevent.
 */
export function effectiveRateFromFlat(flatRate, years) {
    const f = Number(flatRate) || 0;
    const y = Number(years) || 0;
    if (f <= 0 || y <= 0) return Math.max(0, f);
    const n = Math.round(y * 12);
    // P cancels, so any principal gives the same answer; 1 keeps the numbers small.
    const flatMonthly = (1 + f * y) / n;
    let lo = 0, hi = 1;                       // 0% - 100% p.a.
    for (let i = 0; i < 80; i++) {
        const mid = (lo + hi) / 2;
        if (pmt(mid / 12, n, 1) < flatMonthly) lo = mid; else hi = mid;
    }
    return (lo + hi) / 2;
}

/**
 * The same conversion backwards: an EFFECTIVE rate as the FLAT rate that bills the same
 * money. Unlike the forward direction this is closed form - run PMT once, total it over
 * the tenure, and the flat rate is whatever interest that total implies:
 *     flat = (PMT(eff) x months / P - 1) / years          (P cancels, so P = 1)
 *
 * Needed because the Flat / Effective toggle must CONVERT what is in the boxes, not just
 * relabel it. 3.6% read as effective instead of flat is a different, much cheaper loan -
 * on RM 170,000 over 3 years, RM 4,990 a month against RM 5,232. (user 2026-10-16:
 * "default effective rate shd be converted right")
 */
export function flatRateFromEffective(effRate, years) {
    const e = Number(effRate) || 0;
    const y = Number(years) || 0;
    if (e <= 0 || y <= 0) return Math.max(0, e);
    const n = Math.round(y * 12);
    return (pmt(e / 12, n, 1) * n - 1) / y;
}

/**
 * Internal Rate of Return — Newton's method, 100 iterations.
 * Returns the periodic rate where NPV(cashflows) = 0. cashflows[0] is t=0
 * (typically the negative investment), cashflows[N] is end of year N.
 */
export function irr(cashflows, guess = 0.1) {
    if (!cashflows || cashflows.length < 2) return null;
    const npv = (r) =>
        cashflows.reduce((sum, cf, i) => sum + cf / Math.pow(1 + r, i), 0);
    const dNpv = (r) =>
        cashflows.reduce(
            (sum, cf, i) => sum - (i * cf) / Math.pow(1 + r, i + 1),
            0
        );
    let r = guess;
    for (let i = 0; i < 100; i++) {
        const f = npv(r);
        if (Math.abs(f) < 1e-6) return r;
        const df = dNpv(r);
        if (df === 0) break;
        const rNew = r - f / df;
        if (!Number.isFinite(rNew) || rNew < -0.99) return null;
        r = rNew;
    }
    return Number.isFinite(r) ? r : null;
}

// ─────────────────────────────────────────────────────────────────────────
// Main hook
// ─────────────────────────────────────────────────────────────────────────

/**
 * useCscProposalMath — given the user's CSC proposal inputs, produce the
 * full 20-year projection, headline KPIs, environmental impact, and
 * financing scenarios needed by the proposal template.
 *
 * Inputs (all optional except kwp, systemCost, tariffRate):
 *   kwp              system size kWp
 *   systemCost       capex RM (defaults to kwp × DEFAULT_PRICE_PER_KWP if blank)
 *   tariffRate       RM/kWh (one of CSC_PROPOSAL_TARIFFS rates)
 *   omAnnual         O&M RM/yr (defaults to kwp × DEFAULT_OM_PER_KWP_PER_YEAR)
 *   monthlyBill      RM/month (echoed back for display; not used in math today)
 *   includeCA        bool — apply Capital Allowance tax saving (default true)
 *   includeITA       bool — apply Investment Tax Allowance (default true; uses
 *                    a simplified one-shot Y1 60% claim — full ITA needs the
 *                    customer's taxable income which isn't on this form)
 *   includeOutright  bool — include Outright Purchase scenario in the output
 *   includeSARE      bool — include SARE (Solar Lease) scenario
 *   includeLoan      bool — include Loan-financed Outright scenario
 *   sareTariffRate   RM/kWh — what SARE provider charges customer (default = tariffRate × 0.95)
 *   loanPrincipal    RM — defaults to systemCost
 *   loanRate         annual rate (default 0.07)
 *   loanTermMonths   default 120
 */
export default function useCscProposalMath(inputs) {
    return useMemo(() => {
        const {
            kwp = 0,
            systemCost: systemCostRaw,
            tariffRate = 0,
            omAnnual: omAnnualRaw,
            monthlyBill = 0,
            // Year-1 NET saving (annual RM) = real bill reduction from the card. When > 0 it drives
            // the whole projection so the table / average / 20-yr total / ROI tally with the card +
            // WhatsApp. Net degrades on the same generation curve. 0 = fall back to gross gen×tariff.
            year1NetSaving = 0,
            includeCA = true,
            includeITA = true,
            includeOutright = true,
            includeSARE = false,
            includeLoan = false,
            sareTariffRate: sareTariffRateRaw,
            // Each term's own override. Blank falls back to the rate card for that term.
            sareTariffRate15: sareTariffRate15Raw,
            sareTariffRate10: sareTariffRate10Raw,
            // Everything on the job that is NOT the panel system — the battery, the catalogue
            // add-on rows and the flat Add-on Price — because SARE has to recover those over
            // the contract too. (user 2026-10-14: "any addon including battery")
            //
            // It only reaches the FALLBACK below. An explicit sareTariffRate has already been
            // uplifted by the tab that computed it, and applying the uplift a second time here
            // would charge for the add-ons twice.
            sareAddOnCost = 0,
            /* The REAL share of a year's generation this customer uses, worked out month
               by month from their own bills. Null (the default) keeps the existing
               behaviour exactly, so a single-bill quote is unchanged.
               (user 2026-10-24: "bcos the yearly is the same. just the month need to
                settle first" / "ofcos the normal one dont change anytthing") */
            sareSelfUseFraction = null,
            loanPrincipal: loanPrincipalRaw,
            loanRate = DEFAULT_LOAN_RATE,
            loanTermMonths = DEFAULT_LOAN_TERM_MONTHS,
            // Optional extra loan TERMS for a side-by-side comparison, as
            // [{ months, rate?, principal? }]. rate / principal fall back to the single
            // loan's. Empty or absent = today's behaviour, one loan.
            loanTerms,
        } = inputs || {};

        if (!kwp || !tariffRate) {
            return { invalid: true, reason: 'missing kwp or tariffRate' };
        }

        const systemCost = systemCostRaw && systemCostRaw > 0
            ? systemCostRaw
            : kwp * DEFAULT_PRICE_PER_KWP;
        const omAnnual = omAnnualRaw != null && omAnnualRaw >= 0
            ? omAnnualRaw
            : getMaintenanceForKwp(kwp); // tier-based default (mirrors the CSC calculator)
        /* THREE INDEPENDENT RATES, each from the rate card and each separately
           overridable. They used to be one rate plus two premiums, so overriding the
           20-year silently moved the other two - which is wrong now that the card gives
           each term its own number per band.
           (user 2026-10-23: "each 15,10,20years sare rate can you allow editable?
            autopopulate") */
        const sareTariff = sareTariffRateRaw && sareTariffRateRaw > 0
            ? sareTariffRateRaw
            : (defaultSareTariffByKwp(kwp, SARE_20YR_TERM, sareAddOnCost) || tariffRate * DEFAULT_SARE_TARIFF_DISCOUNT);
        const sareTariff15 = sareTariffRate15Raw && sareTariffRate15Raw > 0
            ? sareTariffRate15Raw
            : defaultSareTariffByKwp(kwp, SARE_15YR_TERM, sareAddOnCost);
        const sareTariff10 = sareTariffRate10Raw && sareTariffRate10Raw > 0
            ? sareTariffRate10Raw
            : defaultSareTariffByKwp(kwp, SARE_10YR_TERM, sareAddOnCost);
        const loanPrincipal = loanPrincipalRaw && loanPrincipalRaw > 0
            ? loanPrincipalRaw
            : systemCost;

        // Effective RM/kWh the projection saves. When a year-1 NET saving is supplied, back it out
        // to a per-kWh rate off year-1 generation so every projection year = gen × this rate (net,
        // degrades with gen). Otherwise use the raw tariff (legacy gross model). (user 2026-07-14)
        const _genY1 = annualGeneration(kwp, 1);
        // Cap the effective rate at the tariff — a saving can never exceed gen × tariff.
        const savingRate = (year1NetSaving > 0 && _genY1 > 0) ? Math.min(tariffRate, year1NetSaving / _genY1) : tariffRate;

        // Loan repayment — PMT amortization (reducing balance) to match the
        // Astern bank-loan Excel: PMT(rate/12, months, principal).
        //   Yearly installment = monthly PMT × 12
        //   Total repayment = monthly PMT × months
        //   Total interest = total repayment − principal
        // Spot-check 172,800 @ 5% × 7yr → 2,442/mo · 29,306/yr · 205,156 total ✓
        const loanYears = loanTermMonths / 12;
        const loanMonthlyRate = loanRate / 12;
        const loanMonthlyPmt = includeLoan
            ? pmt(loanMonthlyRate, loanTermMonths, loanPrincipal)
            : 0;
        const loanAnnualPmt = loanMonthlyPmt * 12;
        const loanTotalRepayment = loanMonthlyPmt * loanTermMonths;
        const loanTotalInterest = loanTotalRepayment - (includeLoan ? loanPrincipal : 0);

        // Several loan TERMS on one proposal, so 3 / 5 / 7 years can be compared side by
        // side instead of one at a time. (user 2026-10-15: "can we select also 3,5,7 years
        // loan either one? or choose any two or 3 aso can as comparison")
        //
        // Each term carries its OWN rate — a longer tenure is usually priced higher, the way
        // the Astern hire-purchase sheet runs 3.6 / 3.6 / 4.0%. Passing the same rate three
        // times gives the shared-rate case, so one shape covers both.
        //
        // The single loanTermMonths above is untouched and still drives the existing loan
        // block: a proposal that selects no term behaves exactly as it does today.
        const loanPlans = (Array.isArray(loanTerms) ? loanTerms : [])
            .map(t => {
                const months = Math.round(Number(t && t.months) || 0);
                if (months <= 0) return null;
                const quoted = Number(t.rate != null ? t.rate : loanRate) || 0;
                const principal = Number(t.principal != null ? t.principal : loanPrincipal) || 0;
                // A FLAT rate is converted to its effective twin here and nowhere else, so
                // the projection, the breakeven and every page downstream keep working in
                // one kind of rate. (user 2026-10-16)
                const isFlat = !!t.flat;
                const rate = isFlat ? effectiveRateFromFlat(quoted, months / 12) : quoted;
                const mPmt = pmt(rate / 12, months, principal);
                return {
                    months,
                    years: months / 12,
                    rate,
                    // Both kept: the quoted rate is what the customer was told, the
                    // effective one is what the table ran on. A page that shows only the
                    // converted figure looks like a different deal to the one discussed.
                    quotedRate: quoted,
                    isFlat,
                    principal,
                    monthlyPmt: mPmt,
                    annualPmt: mPmt * 12,
                    totalRepayment: mPmt * months,
                    totalInterest: (mPmt * months) - principal,
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.months - b.months);

        // ITA — Excel scenario tied to customer's taxable income (J1).
        // Without that data we apply a simplified "claim 60% of system cost
        // against Y1 income tax" assumption. ITA gives a one-shot tax credit
        // (cost × 0.60 × 0.24) realized in Year 1.
        const itaCredit = includeITA ? systemCost * 0.60 * CORPORATE_TAX_RATE : 0;

        // ── 20-year projection ──
        const years = [];
        let cumGen = 0;
        let cumOutright = -systemCost;
        let cumLoan = 0;                    // financed — no upfront cash out
        let cumSARE = 0;                    // SARE provider owns system — no upfront
        let cumSARE10 = 0;                  // same, on the 10-year term (years 1-10 only)
        let cumSARE15 = 0;                  // and on the 15-year term (years 1-15 only)

        const outrightCashflows = [-systemCost];
        const loanCashflows = [0];
        const sareCashflows = [0];

        for (let y = 1; y <= PROJECTION_YEARS; y++) {
            const gen = annualGeneration(kwp, y);
            cumGen += gen;

            // Annual solar saving (NET when year1NetSaving supplied, else gross gen×tariff).
            const grossRevenue = gen * savingRate;
            const netSavings = grossRevenue - omAnnual;     // kept for any downstream UI that wants net-of-O&M

            // Capital Allowance tax saving (claim starts year AFTER install).
            const caClaim = includeCA ? caForYear(systemCost, y) : 0;
            const caTaxSaving = caClaim * CORPORATE_TAX_RATE;

            // GITA one-shot credit lands in Y2 (claim begins the year after install).
            const itaTaxSaving = (y === CA_FIRST_CLAIM_YEAR) ? itaCredit : 0;

            // Outright cashflow — matches Excel Breakeven: gross savings + tax
            // incentives, no O&M deduction. O&M is shown to the customer as a
            // separate line in the proposal but isn't netted from Net Return.
            const outrightCF = grossRevenue + caTaxSaving + itaTaxSaving;
            cumOutright += outrightCF;
            outrightCashflows.push(outrightCF);

            // Loan cashflow — same gross savings + tax savings, minus loan payment
            // during the loan term.
            const loanPayThisYear = y <= loanYears ? loanAnnualPmt : 0;
            const loanCF = grossRevenue + caTaxSaving + itaTaxSaving - loanPayThisYear;
            cumLoan += loanCF;
            loanCashflows.push(loanCF);

            // SARE — TNB tariff escalates 8% every 3 years (per SPPA Excel sheet),
            // SARE rate stays flat at the contract value. So the savings GAP
            // widens over time and yearly savings grow despite panel degradation.
            //   Tariff_year = base × 1.08^floor((year-1)/3)
            const tariffYear = tariffRate * Math.pow(1.08, Math.floor((y - 1) / 3));
            const sareRevenue = gen * (tariffYear - sareTariff);
            const sareCF = sareRevenue;
            // NETT SARE: the gross figure above assumes 100% self-consumption. In reality the
            // exported surplus only earns SMP (RM0.20), not the escalating tariff. Back the
            // self-use fraction out of the card's net rate (savingRate), then value the
            // self-used kWh at the escalating tariff and the exported kWh at SMP. (user 2026-08-04)
            /* Prefer the measured mix. The fallback backs a self-use share out of the
               card's single net rate - a reasonable guess from one bill, and no more than
               that. Where twelve months exist, the share is known rather than inferred,
               and it is the quiet months (which export most, and so LOSE 0.215 on every
               exported kWh) that a single-bill guess flatters. */
            const _selfFrac = sareSelfUseFraction != null
                ? Math.min(1, Math.max(0, sareSelfUseFraction))
                : tariffRate > SARE_SMP_RATE
                ? Math.min(1, Math.max(0, (savingRate - SARE_SMP_RATE) / (tariffRate - SARE_SMP_RATE)))
                : 1;
            const _effTariff = _selfFrac * tariffYear + (1 - _selfFrac) * SARE_SMP_RATE;
            const sareNetCF = gen * (_effTariff - sareTariff);
            // Accumulate the NETT figure (2026-08-20). cumSARE and the IRR cashflows
            // previously took sareCF, the gross number that assumes 100% self-consumption,
            // so the 20-year cumulative and the SARE IRR were both overstated by the
            // value of every exported kWh. sareNetCF was computed but never used here.
            cumSARE += sareNetCF;
            sareCashflows.push(sareNetCF);

            // ── 10-year SARE ──
            // IDENTICAL formulas to the 20-year block above (same escalating TNB tariff,
            // same self-use/export split); only the flat lease rate and the horizon differ.
            // Deliberately computed inside this loop rather than in a second pass so the
            // two terms can never drift apart — one place defines how a SARE year is priced.
            const isIn10 = y <= SARE_10YR_TERM;
            const sare10CF = isIn10 ? gen * (tariffYear - sareTariff10) : 0;
            const sare10NetCF = isIn10 ? gen * (_effTariff - sareTariff10) : 0;
            if (isIn10) cumSARE10 += sare10NetCF;

            // ── 15-year SARE ──
            // Same formulas again, at the 15-year rate and horizon. Third term, one
            // definition of how a SARE year is priced.
            const isIn15 = y <= SARE_15YR_TERM;
            const sare15CF = isIn15 ? gen * (tariffYear - sareTariff15) : 0;
            const sare15NetCF = isIn15 ? gen * (_effTariff - sareTariff15) : 0;
            if (isIn15) cumSARE15 += sare15NetCF;

            years.push({
                year: y,
                generation: gen,
                cumGeneration: cumGen,
                grossRevenue,
                omCost: omAnnual,
                netSavings,
                caClaim,
                caTaxSaving,
                itaTaxSaving,
                outright: {
                    annualCF: outrightCF,
                    cumulative: cumOutright,
                },
                loan: {
                    annualCF: loanCF,
                    annualPayment: loanPayThisYear,
                    cumulative: cumLoan,
                },
                tariffYear,
                sare: {
                    annualCF: sareCF,
                    annualNetCF: sareNetCF,
                    sareCost: gen * sareTariff,
                    cumulative: cumSARE,
                    tariffYear,
                    discount: tariffYear - sareTariff,
                },
                // null past year 10 — consumers must not chart a 10-year lease beyond term.
                sare10: isIn10 ? {
                    annualCF: sare10CF,
                    annualNetCF: sare10NetCF,
                    sareCost: gen * sareTariff10,
                    cumulative: cumSARE10,
                    tariffYear,
                    discount: tariffYear - sareTariff10,
                } : null,
                // null past year 15, for the same reason sare10 is null past year 10.
                sare15: isIn15 ? {
                    annualCF: sare15CF,
                    annualNetCF: sare15NetCF,
                    sareCost: gen * sareTariff15,
                    cumulative: cumSARE15,
                    tariffYear,
                    discount: tariffYear - sareTariff15,
                } : null,
                /* LIFE AFTER THE LEASE — the years past the end of ANY term.
                   The lease ends at year 10 and the system is theirs, so there is no
                   longer a per-kWh rate to pay: the saving stops being (tariff − lease)
                   and becomes the WHOLE tariff. Same generation curve, same escalating
                   TNB tariff — only the lease cost disappears, which is exactly why the
                   saving steps up. (user 2026-10-23: "then make it 20years, the
                   generation all same. just the saving wll become more, cause they own
                   the whole thing already")

                   ADDITIVE ON PURPOSE. It is a new field rather than an extension of
                   sare10, because `total10yrSARE`, the WhatsApp summary and the Combined
                   tab all read sare10 and mean "the 10-year lease". Widening sare10 would
                   silently inflate every one of those headline numbers. */
                owned: {
                    annualCF: gen * tariffYear,
                    annualNetCF: gen * _effTariff,
                    sareCost: 0,
                    tariffYear,
                    discount: tariffYear,
                },
            });
        }

        // ── Headline KPIs ──

        // Breakeven year = first year cumulative cashflow turns positive (INTEGER)
        const outrightBreakeven = years.find((y) => y.outright.cumulative >= 0)?.year ?? null;
        const loanBreakeven = years.find((y) => y.loan.cumulative >= 0)?.year ?? null;

        // Exact fractional payback: linear-interpolate WITHIN the breakeven year.
        // If cumulative crosses 0 during year N, payback = (N-1) + |cum_{N-1}|/(cum_N - cum_{N-1}).
        function exactPaybackFrom(startCum, annualCFs) {
            let prev = startCum;
            for (let i = 0; i < annualCFs.length; i++) {
                const cur = prev + annualCFs[i];
                if (cur >= 0) {
                    const fraction = annualCFs[i] > 0 ? -prev / annualCFs[i] : 0;
                    return i + fraction; // years from time 0
                }
                prev = cur;
            }
            return null;
        }

        // Per-year cashflows (excluding the initial -systemCost outlay)
        const cfOutrightWith = years.map((y) => y.outright.annualCF);                    // grossRevenue + CA + GITA
        const cfOutrightNoIncentives = years.map((y) => y.grossRevenue);                 // pure solar revenue, no CA, no GITA
        const cfLoanWith = years.map((y) => y.loan.annualCF);                            // grossRevenue + CA + GITA − loan payment
        const cfLoanNoIncentives = years.map((y) => y.grossRevenue - (y.loan.annualPayment || 0));  // no CA, no GITA

        const outrightBreakevenExact = exactPaybackFrom(-systemCost, cfOutrightWith);
        const outrightBreakevenExactNoIncentives = exactPaybackFrom(-systemCost, cfOutrightNoIncentives);
        // Loan: zero capex outlay (financed by bank), cumulative starts at 0
        const loanBreakevenExact = exactPaybackFrom(0, cfLoanWith);
        const loanBreakevenExactNoIncentives = exactPaybackFrom(0, cfLoanNoIncentives);

        /* ── Each compared term's OWN 20-year series ──────────────────────────────────
           A 3-year loan stops paying in year 3 and a 7-year one does not, so the yearly
           cashflow, the cumulative, the breakeven and the 20-year total all differ per
           term. They used to be taken from the SINGLE loan above and printed on all three
           pages, which put a 7-year repayment schedule on the 3-year page and gave every
           term the same ROI. (user 2026-10-16: "I think the tenure and all is wrong")

           Savings, CA and GITA are shared - only the repayment moves - so each series is
           derived from `years` rather than recomputed from generation. */
        for (const plan of loanPlans) {
            let cum = 0;
            plan.rows = years.map((y) => {
                const annualPayment = y.year <= plan.years ? plan.annualPmt : 0;
                const annualCF = y.grossRevenue + y.caTaxSaving + y.itaTaxSaving - annualPayment;
                cum += annualCF;
                return {
                    year: y.year,
                    generation: y.generation,
                    grossRevenue: y.grossRevenue,
                    caTaxSaving: y.caTaxSaving,
                    itaTaxSaving: y.itaTaxSaving,
                    annualPayment,
                    annualCF,
                    cumulative: cum,
                };
            });
            plan.total20yr = cum;
            plan.breakeven = plan.rows.find((r) => r.cumulative >= 0)?.year ?? null;
            plan.breakevenExact = exactPaybackFrom(0, plan.rows.map((r) => r.annualCF));
            plan.breakevenExactNoIncentives = exactPaybackFrom(
                0, plan.rows.map((r) => r.grossRevenue - r.annualPayment));
        }

        // IRR
        const outrightIRR = irr(outrightCashflows);
        const outrightIRRWithoutCA = irr([
            -systemCost,
            ...years.map((y) => y.grossRevenue - y.omCost),
        ]);

        // 10-year totals (first 10 projection years, degradation-aware) — used by the
        // WhatsApp/breakdown so its 10-yr figure matches the proposal's model.
        const total10yrOutright = years.slice(0, 10).reduce((s, y) => s + y.outright.annualCF, 0);
        // 20-year totals
        const total20yrOutright = years.reduce((s, y) => s + y.outright.annualCF, 0);
        const total20yrLoan = years.reduce((s, y) => s + y.loan.annualCF, 0);
        // NETT, matching cumSARE and the IRR (2026-08-20). sare_20yr_saving is
        // referenced 9 times across the EN/CN/MY templates and sare_20yr_net_saving not
        // at all, so summing the gross annualCF here put an overstated 20-year total in
        // front of customers in every language.
        const total20yrSARE = years.reduce((s, y) => s + (y.sare.annualNetCF != null ? y.sare.annualNetCF : y.sare.annualCF), 0);
        // 10-year lease total — only the first 10 years carry a sare10 block.
        const total10yrSARE = years.reduce((s, y) => s + (y.sare10 ? (y.sare10.annualNetCF != null ? y.sare10.annualNetCF : y.sare10.annualCF) : 0), 0);
        // 15-year lease total — only the first 15 years carry a sare15 block.
        const total15yrSARE = years.reduce((s, y) => s + (y.sare15 ? (y.sare15.annualNetCF != null ? y.sare15.annualNetCF : y.sare15.annualCF) : 0), 0);
        const totalGen20yr = cumGen;

        // Average annual
        const avgAnnualOutright = total20yrOutright / PROJECTION_YEARS;
        const avgAnnualLoan = total20yrLoan / PROJECTION_YEARS;
        const avgAnnualSARE = total20yrSARE / PROJECTION_YEARS;
        const avgAnnualGen = totalGen20yr / PROJECTION_YEARS;

        // LCOE — system cost ÷ total 20-yr generation (no discounting)
        const lcoe = systemCost / totalGen20yr;

        // Year-1 highlights
        const yr1Gen = years[0].generation;
        const yr1MonthlyGen = yr1Gen / 12;
        const yr1MonthlyRevenue = years[0].grossRevenue / 12;
        const yr1MonthlySaving = years[0].netSavings / 12;
        const yr1AnnualSaving = years[0].netSavings;

        // Environmental (computed from Year-1 generation; Excel does the same)
        const co2TonnesPerYear = (CO2_TONNES_PER_MWH * yr1Gen) / 1000;
        const treesSavedPerYear = co2TonnesPerYear * TREES_PER_TONNE_CO2;
        const co2TonnesOver20yr = (CO2_TONNES_PER_MWH * totalGen20yr) / 1000;

        // Reduction percentages
        const yr1BillReductionPct = monthlyBill > 0
            ? Math.min(1, yr1MonthlyRevenue / monthlyBill)
            : null;

        // CA / ITA totals
        const cumCAOverYears = years.reduce((s, y) => s + y.caClaim, 0);
        const cumCATaxSaving = years.reduce((s, y) => s + y.caTaxSaving, 0);

        return {
            invalid: false,

            // Echoed inputs
            kwp,
            systemCost,
            tariffRate,
            omAnnual,
            monthlyBill,
            includeCA,
            includeITA,
            includeOutright,
            includeSARE,
            includeLoan,
            sareTariff,
            sareTariff10,
            sareTariff15,
            total10yrSARE,
            total15yrSARE,
            loanPrincipal,
            loanRate,
            loanTermMonths,
            loanMonthlyPmt,
            loanAnnualPmt,
            // One entry per selected term, shortest first. Empty on a proposal that has
            // not asked for a comparison.
            loanPlans,
            itaCredit,

            // Year-1 headlines
            yr1Generation: yr1Gen,
            yr1MonthlyGen,
            yr1MonthlyRevenue,
            yr1MonthlySaving,
            yr1AnnualSaving,
            yr1BillReductionPct,

            // 10/20-year totals
            totalGen20yr,
            total10yrOutright,
            total20yrOutright,
            total20yrLoan,
            total20yrSARE,
            avgAnnualOutright,
            avgAnnualLoan,
            avgAnnualSARE,
            avgAnnualGen,

            // Breakeven + ROI
            outrightBreakeven,
            loanBreakeven,
            outrightBreakevenExact,
            outrightBreakevenExactNoIncentives,
            loanBreakevenExact,
            loanBreakevenExactNoIncentives,
            outrightIRR,
            outrightIRRWithoutCA,
            lcoe,
            cumCAOverYears,
            cumCATaxSaving,

            // Environmental
            co2TonnesPerYear,
            treesSavedPerYear,
            co2TonnesOver20yr,

            // Full projection table
            years,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(inputs)]);
}

/**
 * SARE early-buyback for year `yearIdx` (0-based) of a `termYears` contract, as a
 * MULTIPLE OF CAPEX.
 *
 * The schedule is Astern's own (Commercial Calculation Sheet, SPPA sheet "Revised
 * Buyback" O7:O26): year 1 = capex × 1.5, then each year = the previous year × a fixed
 * factor. It is written for a TWENTY-year contract and only falls steeply in years 16-20
 * (0.90, 0.80, 0.70, 0.65, 0.60).
 *
 * EVERY TERM USED TO READ ITS FIRST N YEARS, which left the buyback high at the exact
 * moment a zero-capex system transfers for nothing:
 *
 *     10-year ended at 117% of capex  ┐
 *     15-year ended at  88% of capex  ├ and the next row said "Owned"
 *     20-year ended at  17% of capex  ┘
 *
 * A ten-year customer was quoted MORE THAN THE SYSTEM COST NEW to buy out in year 10,
 * and nothing at all in year 11. On a zero-capex product that is not a schedule, it is
 * a cliff.
 *
 * A shorter term now reads the SAME curve at the same fraction of the way through, so
 * its final year lands where year 20 lands and the shape between is preserved. The
 * 20-year table is untouched. Nothing is invented: it is the documented curve, stretched
 * — pending Astern's own 10- and 15-year schedules should they differ.
 * (user 2026-10-26: "10years and 15years is zero capex sare one ... i think urs 10years
 *  sare is wrong the buyback")
 */
export const SARE_BUYBACK_FACTORS = [1.5, 0.98, 0.98, 0.98, 0.98, 0.98, 0.98, 0.98, 0.98, 0.92,
                                     0.95, 0.95, 0.95, 0.92, 0.95, 0.90, 0.80, 0.70, 0.65, 0.60];

/** The 20-year schedule at a 0-based year index, as a multiple of capex. */
function buybackCurve(yearIdx) {
    let v = SARE_BUYBACK_FACTORS[0];
    for (let i = 1; i <= yearIdx && i < SARE_BUYBACK_FACTORS.length; i++) v *= SARE_BUYBACK_FACTORS[i];
    return v;
}

export function sareBuybackFactor(yearIdx, termYears) {
    const N = SARE_BUYBACK_FACTORS.length;
    const n = Number(termYears) || N;
    const idx = Math.max(0, Number(yearIdx) || 0);
    if (n >= N || n <= 1) return buybackCurve(idx);
    const p = idx * ((N - 1) / (n - 1));
    const lo = Math.min(N - 1, Math.floor(p));
    const hi = Math.min(N - 1, lo + 1);
    return buybackCurve(lo) + (buybackCurve(hi) - buybackCurve(lo)) * (p - lo);
}
